import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Divider } from "@mui/material";
import { Legend } from "../../../../common/ChartLegend/ChartLegend";
import { dailyOccupancySelector } from "../../../../../redux/selectors/occupancyRateSelector";
import { isMobileModeSelector } from "../../../../../redux/selectors/appStatusSelector";
import classes from "./DailyOccupancyChart.module.scss";
import { performancePagePeriodSelector } from "../../../../../redux/selectors/performancePageSelector";
import { Typography } from "@/shared/ui/typography";
import clsx from "clsx";
import { mapDataForDailyOccupancy } from "@/utils/mapDataForDailyOccupancy";

const CustomTooltip = (props: any) => {
  const {
    active,
    payload,
    viewBox,
    isMobileView,
    barWidth,
    activeTooltipIndex,
    leftTooltipRef,
  } = props;

  if (active && payload && payload?.length) {
    const leftDataPoint = payload[0].payload;
    const leftName = payload[0].name;

    return (
      <>
        <div
          className={`${classes.tooltip} ${classes.rightTooltip}`}
          style={{
            bottom: isMobileView
              ? -barWidth * 3 -
                (viewBox.y + barWidth / 2) -
                barWidth *
                  (activeTooltipIndex || 0) *
                  (activeTooltipIndex === 0 ? 1 : 2) +
                barWidth -
                (activeTooltipIndex || 0) / 2 +
                barWidth / 2 -
                (activeTooltipIndex || 0) * 4 +
                3
              : -barWidth * 3 -
                (viewBox.y + barWidth / 2) -
                barWidth *
                  (activeTooltipIndex || 0) *
                  (activeTooltipIndex === 0 ? 1 : 2) +
                barWidth -
                (activeTooltipIndex || 0) / 2 +
                barWidth / 2 -
                1,
            left: viewBox.width / 2,
          }}
          ref={leftTooltipRef}
        >
          <Typography
            variant="text-sm"
            weight="medium"
            className={classes.gray900}
          >
            {`${leftDataPoint[leftName]}%`}
          </Typography>
        </div>
      </>
    );
  }

  return null;
};

export const DailyOccupancyChart = () => {
  const period = useSelector(performancePagePeriodSelector);
  const data = useSelector(dailyOccupancySelector);
  const isMobileView = useSelector(isMobileModeSelector);

  const occupancyBarLegend = [
    {
      label: "Occupancy Rate",
      color: "#8098F9",
      legendType: "circle",
    },
  ];

  const displayData = data && mapDataForDailyOccupancy(data, period);

  const barWidth = 16;
  const responsiveContainerRef = useRef<any>(null);
  const leftTooltipRef = useRef<any>(null);

  const [activeTooltipIndex, setActiveTooltipIndex] = useState<
    number | undefined
  >();

  if (!data || Object.keys(data).length === 0) {
    return (
      <div
        className={clsx(
          classes.occupancyChartContainer,
          classes.errorMessageContainer
        )}
      >
        <Typography
          variant="text-lg"
          weight="medium"
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.occupancyChartContainer}>
      <div className={classes.occupancyChartHeader}>
        <div className={classes.occupancyLegendContainer}>
          <Typography
            variant="text-md"
            weight="semibold"
            className={classes.gray900}
          >
            Occupancy Rate by day
          </Typography>
          {!isMobileView ? <Divider flexItem orientation="vertical" /> : null}
        </div>
      </div>
      {data && (
        <ResponsiveContainer
          width="100%"
          height={isMobileView ? 202 : "80%"}
          ref={responsiveContainerRef}
        >
          <BarChart
            layout="vertical"
            data={displayData}
            margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
            onMouseMove={({ activeTooltipIndex }) =>
              setActiveTooltipIndex(activeTooltipIndex)
            }
            onMouseLeave={() => setActiveTooltipIndex(undefined)}
          >
            <XAxis axisLine={false} tickLine={false} type="number" unit="%" />
            <YAxis
              axisLine={false}
              dataKey="Day_of_week"
              tickLine={false}
              type="category"
              width={isMobileView ? 44 : 60}
            />
            <Tooltip
              position={{
                x: 0,
                y: 0,
              }}
              content={
                <CustomTooltip
                  isMobileView={isMobileView}
                  barWidth={barWidth}
                  activeTooltipIndex={activeTooltipIndex}
                  leftTooltipRef={leftTooltipRef}
                />
              }
              cursor={false}
              allowEscapeViewBox={{ x: true, y: true }}
            />
            <Bar
              barSize={16}
              dataKey="occupancy_rate"
              isAnimationActive={false}
              fill="#8098F9"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
      {isMobileView ? (
        <Legend className={classes.legend} legendData={occupancyBarLegend} />
      ) : null}
    </div>
  );
};
